import { Subject } from 'rxjs';
import { Batch } from './batch';
import { BatchResult } from './batch-result';
import { CoordinatorService } from './coordinator.service';
import { Utils$ } from './utils$';
import { XItem } from './x-item';

export type CREATE_BATCH = { id?: string; payload?: any };
export type BATCH_CACHE_TYPE = 'use' | 'ignore';

export class Batch$ {
    counter = 1;

    await$ = new Subject<any>();

    private _batchMap = new Map<string, Batch>();

    private _resultMap = new Map<string, BatchResult>();

    private _tmpStore = new Map<string, Map<string, any>>();

    constructor(public coords: CoordinatorService) {}

    serviceLs = ['altmetric', 'crossref', 'open-alex', 'semantic-scholar', 'unpaywall'];

    dialogLs = ['biblio', 'fulltext', 'doi', 'orcid', 'raw'];

    private _actionLsMap = new Map<string, string[]>([['data-check', ['biblio']]]);

    onStart() {
        this.coords.worker$.batch.response$.subscribe({
            next: (x) => !x.mId && this._workerResponse(x.payload),
            error: (e) => console.log(e),
        });

        this.coords.worker$.batch.reportSuccess$.subscribe({
            next: (x) => !x.mId && this._workerResponse(x),
            error: (e) => console.log(e),
        });
    }

    start({
        serviceLs,
        actionLs,
        itemLs,
        cache,
    }: {
        serviceLs: string[];
        actionLs: string[];
        itemLs?: any[];
        cache?: BATCH_CACHE_TYPE;
    }) {
        // console.log('Start batch', serviceLs, actionLs, cache);
        if (!itemLs)
            itemLs = this.coords.resultList$.activeList$
                .getValue()
                .map((xitm) => xitm.exportMemowl());
        const b = this.create({ payload: { serviceLs, actionLs, itemLs, cache } });
        b.start();
    }

    runNamedBatch(name: string, params: any) {
        let b: Batch;
        switch (name) {
            case 'open-access':
                b = new Batch(this.coords, {
                    payload: {
                        serviceLs: [params.service || 'open-alex'],
                        actionLs: ['run'],
                        cache: params.cache || 'use',
                        itemLs: params.hitset.mainXitemLs.map((xitm: XItem) => xitm.exportMemowl()),
                    },
                });
                break;
            default:
                b = new Batch(this.coords, {
                    payload: {
                        namedBatch: name,
                        params,
                    },
                });
        }

        b.start();
    }

    setTmpStore({ batchId, payload }: { batchId: string; payload: any }) {
        this._tmpStore.set(batchId, payload);
    }

    getTmpStore(id: string) {
        return this._tmpStore.get(id);
    }

    actionLs(type: string) {
        return this._actionLsMap.get(type) || [];
    }

    clear() {
        this.counter = 1;
        this._batchMap.clear();
        this._resultMap.clear();
    }

    create(params?: CREATE_BATCH): Batch {
        const b = new Batch(this.coords, params || {});
        this._batchMap.set(b.id, b);
        return b;
    }

    get(batchId: string) {
        return this._batchMap.get(batchId);
    }

    test() {
        const b = this.create();
        b.test();
    }

    private _displayResult(
        batchId: string,
        {
            type,
            service,
            data,
            respTarget,
        }: { type: string; service: string; data: any; respTarget: any },
    ) {
        const tm = this.coords.utils$.nowTimeString(true);
        // const title = `${this.counter++}-${service}`;
        const title = `${this.coords.text(type)} (${tm})`;
        // console.log('DISPLAY RESULT', title, data);
        const br = new BatchResult(this.coords, { batchId, title, data, type, service });

        if (respTarget?.type === 'await') {
            this.await$.next({ data, type, respTarget });
            return;
        }

        this._resultMap.set(br.id, br);
        if (!respTarget) {
            const bf = this.coords.filter$.batchFilter(br);
            bf.setBatchValues(br);
            bf.open();
            const vLs = bf.valueLs;
            const sbf = bf.scriptClass;
            sbf.require(vLs[0][0]);
        }
        // const w = this.coords.script$.widgetGet('filter-board');
        // console.log('WWW', w);
        // w?.open("aaa");
        this.coords.display$.markForCheck();
    }

    private async _readResult({
        projectId,
        resultId,
        batchId,
        type,
        service,
        respTarget,
    }: {
        projectId: string;
        resultId: string;
        batchId: string;
        type: string;
        service: string;
        respTarget: any;
    }) {
        const r = await navigator.storage.getDirectory();
        const bH = await r.getDirectoryHandle('_batch');
        const bProjectH = await bH.getDirectoryHandle(projectId);
        const resultDirH = await bProjectH.getDirectoryHandle('_results');
        const resultH = await resultDirH.getFileHandle(resultId);
        const resultF = await resultH.getFile();

        const resultParams: any = { type, service, respTarget };
        if (!['raw'].includes(type)) resultParams.data = JSON.parse(await resultF.text());

        console.log('RESULT TYPE', type, respTarget);
        switch (type) {
            case 'doi':
            case 'biblio':
            case 'orcid':
            case 'fulltext':
                this._displayResult(batchId, resultParams);
                break;
            default:
                console.log('Result', type, JSON.parse(await resultF.text()));
        }
    }

    get batchResultLs() {
        return Array.from(this._resultMap.values());
    }

    getResult(tag: string) {
        return this._resultMap.get(tag);
    }

    private _workerResponse(params: any) {
        if (!params) return;
        if (params.mId) return;
        switch (params.action) {
            case 'result':
                // console.log('Result received', params);
                this._readResult(params);
                break;
            case 'initialized':
                break;

            default:
                this.coords.utils$.consoleInfo({
                    tag: 'BATCH Response',
                    background: 'blue',
                    color: 'orange',
                    payload: params,
                });
        }
    }

    bulkSelect(type: string, disableCache = false) {
        if (['raw'].includes(type))
            return this.coords.display$.dialog$.next({
                action: 'batch-select',
                type,
            });
        const cache = disableCache ? 'disable' : 'use';
        const params: any = {
            type,
            cache,
            itemLs: this.coords.resultList$.activeList$
                .getValue()
                .map((xitm: XItem) => xitm.exportMemowl()),
        };

        const b = new Batch(this.coords, { payload: params });
        b.start();
    }
}
